import { useState, useEffect } from "react";
import { useSeoMeta } from "@unhead/react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Lock,
  CheckCircle2,
  XCircle,
  BrainCircuit,
  RotateCcw,
  PartyPopper,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Confetti } from "@/components/Confetti";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import {
  QUIZ,
  QUIZ_PASS_COUNT,
  type QuizQuestion,
} from "@/lib/curriculum";
import { useProgress } from "@/hooks/useProgress";
import { track } from "@/lib/track";

type Phase = "intro" | "playing" | "done";

export default function QuizPage() {
  const { allLessonsComplete, submitQuiz, quizBestScore, quizPassed } =
    useProgress();

  useSeoMeta({
    title: "Final Quiz — BCH School",
    description:
      "Test everything you've learned about Bitcoin Cash and unlock your certificate.",
  });

  const [phase, setPhase] = useState<Phase>("intro");
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [passedThisRun, setPassedThisRun] = useState(false);

  // Guard: quiz is locked until every lesson is complete.
  if (!allLessonsComplete) {
    return (
      <Layout>
        <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
          <Link to="/">
            <ArrowLeft className="mr-1 h-4 w-4" /> Home
          </Link>
        </Button>
        <Card className="border-2 border-dashed">
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary">
              <Lock className="h-7 w-7 text-muted-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Quiz locked</h1>
              <p className="mx-auto mt-2 max-w-sm text-muted-foreground">
                Complete all the lessons first, then come back to take the final
                quiz and unlock your certificate.
              </p>
            </div>
            <Button asChild size="lg">
              <Link to="/">Keep learning</Link>
            </Button>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  const question: QuizQuestion = QUIZ[current];
  const isLast = current === QUIZ.length - 1;

  function startQuiz() {
    track("quiz_start");
    setPhase("playing");
    setCurrent(0);
    setSelected(null);
    setAnswered(false);
    setScore(0);
  }

  function choose(optionId: string) {
    if (answered) return;
    setSelected(optionId);
    setAnswered(true);
    if (optionId === question.answer) {
      setScore((s) => s + 1);
    }
  }

  function next() {
    if (isLast) {
      finish();
      return;
    }
    setCurrent((c) => c + 1);
    setSelected(null);
    setAnswered(false);
  }

  function finish() {
    const finalScore = score; // score already reflects the last answer
    const passed = submitQuiz(finalScore);
    setPassedThisRun(passed);
    track(passed ? "quiz_pass" : "quiz_fail");
    setPhase("done");
  }

  return (
    <Layout>
      <Confetti active={phase === "done" && passedThisRun} />
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/">
          <ArrowLeft className="mr-1 h-4 w-4" /> Home
        </Link>
      </Button>

      {phase === "intro" && (
        <IntroCard
          onStart={startQuiz}
          alreadyPassed={quizPassed}
          bestScore={quizBestScore}
        />
      )}

      {phase === "playing" && (
        <div className="space-y-6">
          {/* Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm font-semibold">
              <span className="text-muted-foreground">
                Question {current + 1} of {QUIZ.length}
              </span>
              <span className="text-primary">Score: {score}</span>
            </div>
            <Progress
              value={((current + (answered ? 1 : 0)) / QUIZ.length) * 100}
              className="h-2"
            />
          </div>

          <Card className="border-2">
            <CardContent className="space-y-5 p-6">
              <div className="flex items-start gap-2 text-primary">
                <BrainCircuit className="mt-0.5 h-5 w-5 shrink-0" />
                <h1 className="text-xl font-bold leading-snug text-foreground">
                  {question.question}
                </h1>
              </div>

              <div className="space-y-3">
                {question.options.map((opt) => {
                  const isCorrect = opt.id === question.answer;
                  const isChosen = opt.id === selected;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => choose(opt.id)}
                      disabled={answered}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-2xl border-2 bg-card p-4 text-left font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        !answered &&
                          "hover:-translate-y-0.5 hover:border-primary hover:shadow-md",
                        answered &&
                          isCorrect &&
                          "border-primary bg-primary/10",
                        answered &&
                          isChosen &&
                          !isCorrect &&
                          "border-destructive bg-destructive/10",
                        answered &&
                          !isCorrect &&
                          !isChosen &&
                          "opacity-60",
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold uppercase",
                          answered && isCorrect
                            ? "bg-primary text-primary-foreground"
                            : answered && isChosen && !isCorrect
                              ? "bg-destructive text-destructive-foreground"
                              : "bg-secondary text-muted-foreground",
                        )}
                      >
                        {answered && isCorrect ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : answered && isChosen && !isCorrect ? (
                          <XCircle className="h-4 w-4" />
                        ) : (
                          opt.id
                        )}
                      </span>
                      <span className="flex-1">{opt.label}</span>
                    </button>
                  );
                })}
              </div>

              {answered && (
                <div className="rounded-2xl bg-secondary/60 p-4 text-sm leading-relaxed">
                  <span className="font-bold">
                    {selected === question.answer
                      ? "Correct! "
                      : "Not quite. "}
                  </span>
                  {question.explanation}
                </div>
              )}
            </CardContent>
          </Card>

          {answered && (
            <Button size="lg" className="w-full text-base sm:w-auto" onClick={next}>
              {isLast ? "See my result" : "Next question"}
              <ArrowRight className="ml-1 h-5 w-5" />
            </Button>
          )}
        </div>
      )}

      {phase === "done" && (
        <ResultCard
          score={score}
          total={QUIZ.length}
          passed={passedThisRun}
          onRetry={startQuiz}
        />
      )}
    </Layout>
  );
}

function IntroCard({
  onStart,
  alreadyPassed,
  bestScore,
}: {
  onStart: () => void;
  alreadyPassed: boolean;
  bestScore: number | null;
}) {
  return (
    <Card className="border-2">
      <CardContent className="flex flex-col items-center gap-5 py-12 px-6 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-md">
          <BrainCircuit className="h-10 w-10" />
        </div>
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">
            The Final Quiz
          </h1>
          <p className="mx-auto mt-2 max-w-md text-muted-foreground">
            {QUIZ.length} quick questions covering everything you've learned.
            Score {QUIZ_PASS_COUNT} or more to pass and unlock your certificate.
            You can retake it as many times as you like.
          </p>
        </div>

        {alreadyPassed && (
          <div className="flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">
            <CheckCircle2 className="h-4 w-4" /> Already passed
            {bestScore !== null && ` · best ${bestScore}/${QUIZ.length}`}
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-3">
          <Button size="lg" className="text-base" onClick={onStart}>
            {alreadyPassed ? "Retake the quiz" : "Start the quiz"}
            <ArrowRight className="ml-1 h-5 w-5" />
          </Button>
          {alreadyPassed && (
            <Button asChild size="lg" variant="outline" className="text-base">
              <Link to="/certificate">Go to certificate 🎓</Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ResultCard({
  score,
  total,
  passed,
  onRetry,
}: {
  score: number;
  total: number;
  passed: boolean;
  onRetry: () => void;
}) {
  const pct = Math.round((score / total) * 100);
  return (
    <Card
      className={cn(
        "border-2",
        passed ? "border-primary/40 bg-primary/5" : "border-border",
      )}
    >
      <CardContent className="flex flex-col items-center gap-5 py-12 px-6 text-center">
        <div
          className={cn(
            "flex h-20 w-20 items-center justify-center rounded-3xl text-white shadow-md",
            passed
              ? "bg-gradient-to-br from-green-500 to-emerald-600"
              : "bg-gradient-to-br from-slate-400 to-slate-600",
          )}
        >
          {passed ? (
            <PartyPopper className="h-10 w-10" />
          ) : (
            <RotateCcw className="h-10 w-10" />
          )}
        </div>

        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">
            {passed ? "You passed! 🎉" : "So close!"}
          </h1>
          <p className="mx-auto mt-2 max-w-md text-muted-foreground">
            {passed
              ? "You've proven you know your Bitcoin Cash. Your certificate is unlocked — go claim it!"
              : `You scored ${score}/${total}. Review the lessons and give it another go — you've got this.`}
          </p>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-4xl font-extrabold text-primary tabular-nums">
              {score}/{total}
            </div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Correct
            </div>
          </div>
          <div className="h-12 w-px bg-border" />
          <div className="text-center">
            <div className="text-4xl font-extrabold text-primary tabular-nums">
              {pct}%
            </div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Score
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          {passed ? (
            <Button asChild size="lg" className="text-base">
              <Link to="/certificate">Get my certificate 🎓</Link>
            </Button>
          ) : (
            <Button size="lg" className="text-base" onClick={onRetry}>
              <RotateCcw className="mr-1 h-5 w-5" /> Try again
            </Button>
          )}
          <Button asChild size="lg" variant="ghost" className="text-base">
            <Link to="/">Back to lessons</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
